import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useJazyk } from '../context/LanguageContext';
import { Bold, Italic, Link2, List, Table2, Palette } from 'lucide-react';

const TEXT_COLORS = [
  { klic: 'ed.barva.cerna', value: '#2F3441' },
  { klic: 'ed.barva.seda', value: '#5D6472' },
  { klic: 'ed.barva.fialova', value: '#8A7CFF' },
  { klic: 'ed.barva.ruzova', value: '#F5A9B8' },
  { klic: 'ed.barva.modra', value: '#5BCEFA' },
  { klic: 'ed.barva.zelena', value: '#6FE3C1' },
  { klic: 'ed.barva.cervena', value: '#E53935' },
  { klic: 'ed.barva.oranzova', value: '#F57C00' },
];

/**
 * RichTextEditor — contentEditable-based rich text editor.
 * Supports: H1, H2, H3, P, Bold, Italic, Bullet list, Link, Table.
 * Outputs / accepts sanitized HTML string.
 */
export const RichTextEditor = ({ value, onChange, placeholder, rows = 8 }) => {
  const { t } = useJazyk();
  const editorRef = useRef(null);
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  // Track the last value we set externally (to detect parent-driven resets)
  const lastExternalValue = useRef(value);
  const isComposingInput = useRef(false);

  // Only update innerHTML when the value is changed externally (e.g. form reset)
  // Never during user typing — that would jump the cursor to the start.
  useEffect(() => {
    if (!editorRef.current) return;
    const currentHtml = editorRef.current.innerHTML;
    const incomingValue = value || '';
    // If value was reset to '' externally, clear the editor
    if (incomingValue !== lastExternalValue.current && !isComposingInput.current) {
      if (incomingValue !== currentHtml) {
        editorRef.current.innerHTML = incomingValue;
        lastExternalValue.current = incomingValue;
      }
    }
  }, [value]);

  const exec = useCallback((cmd, arg = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    const newVal = editorRef.current?.innerHTML || '';
    lastExternalValue.current = newVal;
    if (onChange) onChange(newVal);
  }, [onChange]);

  const handleInput = useCallback(() => {
    isComposingInput.current = true;
    const newVal = editorRef.current?.innerHTML || '';
    lastExternalValue.current = newVal;
    if (onChange) onChange(newVal);
    // Reset flag after the current event cycle
    requestAnimationFrame(() => { isComposingInput.current = false; });
  }, [onChange]);

  const insertLink = useCallback(() => {
    const sel = window.getSelection();
    const selectedText = sel?.toString() || '';
    const url = window.prompt('URL odkazu (např. https://example.com):', 'https://');
    if (!url) return;
    editorRef.current?.focus();
    if (selectedText) {
      document.execCommand('createLink', false, url);
    } else {
      document.execCommand('insertHTML', false,
        `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`);
    }
    const newVal = editorRef.current?.innerHTML || '';
    lastExternalValue.current = newVal;
    if (onChange) onChange(newVal);
  }, [onChange]);

  const insertTable = useCallback(() => {
    const r = Math.min(Math.max(parseInt(tableRows) || 3, 1), 10);
    const c = Math.min(Math.max(parseInt(tableCols) || 3, 1), 10);
    const headers = Array.from({ length: c }, (_, i) =>
      `<th style="border:1px solid #d1d5db;padding:6px 10px;background:#f5f3ff;font-weight:600;text-align:left">Sloupec ${i + 1}</th>`
    ).join('');
    const cells = Array.from({ length: c }, () =>
      `<td style="border:1px solid #d1d5db;padding:6px 10px">&nbsp;</td>`
    ).join('');
    const bodyRows = Array.from({ length: r }, () => `<tr>${cells}</tr>`).join('');
    const tableHtml =
      `<table style="border-collapse:collapse;width:100%;margin:0.75rem 0">` +
      `<thead><tr>${headers}</tr></thead>` +
      `<tbody>${bodyRows}</tbody></table><p></p>`;
    editorRef.current?.focus();
    document.execCommand('insertHTML', false, tableHtml);
    const newVal = editorRef.current?.innerHTML || '';
    lastExternalValue.current = newVal;
    if (onChange) onChange(newVal);
    setShowTablePicker(false);
  }, [tableRows, tableCols, onChange]);

  const applyTextColor = useCallback((color) => {
    editorRef.current?.focus();
    document.execCommand('foreColor', false, color);
    const newVal = editorRef.current?.innerHTML || '';
    lastExternalValue.current = newVal;
    if (onChange) onChange(newVal);
    setShowColorPicker(false);
  }, [onChange]);

  const minHeight = `${Math.max(rows * 1.5, 6)}rem`;
  const maxHeight = `${Math.max(rows * 2.5, 15)}rem`;

  // Set initial content on mount only (avoids cursor-reset on re-render)
  React.useEffect(() => {
    if (editorRef.current && value) {
      editorRef.current.innerHTML = value;
      lastExternalValue.current = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Sep = () => <div className="w-px h-5 bg-border mx-0.5 shrink-0" />;

  const ToolBtn = ({ title, onAction, children, testId }) => (
    <button
      type="button"
      title={title}
      onMouseDown={e => { e.preventDefault(); onAction(); }}
      data-testid={testId}
      className="px-1.5 h-7 min-w-[1.75rem] rounded text-xs font-semibold transition-colors hover:bg-muted text-bloom-text flex items-center justify-center"
    >
      {children}
    </button>
  );

  return (
    <div className="border border-input rounded-md overflow-visible focus-within:ring-1 focus-within:ring-ring relative">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-muted/50 border-b border-border/50">
        {/* Block formatting */}
        <ToolBtn title="Nadpis 1" onAction={() => exec('formatBlock', 'h1')} testId="editor-h1">H1</ToolBtn>
        <ToolBtn title="Nadpis 2" onAction={() => exec('formatBlock', 'h2')} testId="editor-h2">H2</ToolBtn>
        <ToolBtn title="Nadpis 3" onAction={() => exec('formatBlock', 'h3')} testId="editor-h3">H3</ToolBtn>
        <ToolBtn title="Odstavec" onAction={() => exec('formatBlock', 'p')} testId="editor-p">P</ToolBtn>
        <Sep />
        {/* Inline formatting */}
        <ToolBtn title={t('ed.tucne')} onAction={() => exec('bold')} testId="editor-bold">
          <Bold className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn title={t('ed.kurziva')} onAction={() => exec('italic')} testId="editor-italic">
          <Italic className="w-3.5 h-3.5" />
        </ToolBtn>
        <Sep />
        {/* List */}
        <ToolBtn title={t('ed.odrazky')} onAction={() => exec('insertUnorderedList')} testId="editor-ul">
          <List className="w-3.5 h-3.5" />
        </ToolBtn>
        <Sep />
        {/* Link */}
        <ToolBtn title={t('ed.vlozitOdkaz')} onAction={insertLink} testId="editor-link">
          <Link2 className="w-3.5 h-3.5" />
        </ToolBtn>
        <Sep />
        {/* Text color */}
        <div className="relative">
          <ToolBtn
            title="Barva textu"
            onAction={() => { setShowTablePicker(false); setShowColorPicker(v => !v); }}
            testId="editor-color"
          >
            <Palette className="w-3.5 h-3.5" />
          </ToolBtn>
          {showColorPicker && (
            <div
              className="absolute top-9 left-0 z-50 bg-white border border-border rounded-lg shadow-xl p-2 w-40"
              onMouseDown={e => e.stopPropagation()}
            >
              <p className="text-xs font-semibold text-bloom-text mb-2">Barva textu</p>
              <div className="grid grid-cols-4 gap-1">
                {TEXT_COLORS.map(({ name, value }) => (
                  <button
                    key={value}
                    type="button"
                    title={name}
                    onClick={() => applyTextColor(value)}
                    className="w-8 h-8 rounded border border-border hover:ring-2 hover:ring-bloom-violet transition-all"
                    style={{ backgroundColor: value }}
                    data-testid={`color-${value.replace('#', '')}`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <Sep />
        {/* Table with mini popup */}
        <div className="relative">
          <ToolBtn
            title={t('ed.vlozitTabulku')}
            onAction={() => { setShowColorPicker(false); setShowTablePicker(v => !v); }}
            testId="editor-table"
          >
            <Table2 className="w-3.5 h-3.5" />
          </ToolBtn>
          {showTablePicker && (
            <div
              className="absolute top-9 left-0 z-50 bg-white border border-border rounded-lg shadow-xl p-3 w-48"
              onMouseDown={e => e.stopPropagation()}
            >
              <p className="text-xs font-semibold text-bloom-text mb-2">{t('ed.vlozitTabulku')}</p>
              <div className="flex gap-2 mb-3">
                <div className="flex-1">
                  <label className="text-xs text-bloom-sub block mb-0.5">{t('ed.radky')}</label>
                  <input
                    type="number" min="1" max="10"
                    value={tableRows}
                    onChange={e => setTableRows(e.target.value)}
                    className="w-full border border-border rounded px-2 py-1 text-sm"
                    data-testid="table-rows-input"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-bloom-sub block mb-0.5">Sloupce</label>
                  <input
                    type="number" min="1" max="10"
                    value={tableCols}
                    onChange={e => setTableCols(e.target.value)}
                    className="w-full border border-border rounded px-2 py-1 text-sm"
                    data-testid="table-cols-input"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={insertTable}
                className="w-full bg-bloom-violet text-white text-xs py-1.5 rounded hover:bg-bloom-violet/90 transition-colors"
                data-testid="table-insert-btn"
              >
                {t('ed.vlozit')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Editor content area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        data-placeholder={placeholder}
        data-testid="rich-editor-area"
        className="px-3 py-2 text-sm text-bloom-text outline-none overflow-y-auto leading-relaxed rich-editor"
        style={{ minHeight, maxHeight }}
      />
    </div>
  );
};
